import { useDispatch, useSelector } from 'react-redux';

import { Fragment, useState } from 'react';
import { TabLabel } from 'pages/Workshop/components/TabLabel';
import { ArchiveDropdown } from 'pages/Workshop/components/ArchiveDropdown';
import { WorkshopPageTabs } from 'pages/Workshop/components/WorkshopPageTabs';

import { defaultPagination, FORM_TABS, formsEmptyText, getTabStatusByValue } from './helper';

import { getUserRolesMapSelector } from 'store/selectors/user';
import { useManageFormTabs } from './useManageFormTabs';
import { getActiveFormTabSelector } from 'store/selectors/requestFormProcess';
import { setFormsTabEffect } from 'store/effects/formProcess';

import { FormPaginatedTableWrapper } from '../FormPaginatedTableWrapper';
import { FormsStatuses, SortedColumnStatuses } from 'app-constants';

export const FormTabs = (props) => {
  const { isClient, isThirdParty } = useSelector(getUserRolesMapSelector);

  const [archivedValue, setArchivedValue] = useState('');

  const isClientOrThidParty = isClient || isThirdParty;

  const { activeFormTab } = useSelector(getActiveFormTabSelector);

  const [activeKey, setActiveKey] = useState<any>(
    activeFormTab || (isClientOrThidParty ? FORM_TABS.WaitingOnYou : FORM_TABS.Draft),
  );

  const dispatch = useDispatch();

  const { formsData, formsPagination, isPending, ...restProps } = props;
  const columnKey = archivedValue || activeKey;
  const { tableProps, loading } = useManageFormTabs({
    ...restProps,
    activeKey,
    isPending,
    sortColumnStatus: SortedColumnStatuses[columnKey],
  });

  const {
    recentlyCompleted: recentlyCompletedPagination = defaultPagination,
    finalizing: finalizingPagination = defaultPagination,
    waitingOnYou: waitingOnYouPagination = defaultPagination,
    waitingOnOthers: waitingOnOthersPagination = defaultPagination,
    voided: voidedPagination = defaultPagination,
    draft: draftPagination = defaultPagination,
    declined: declinedPagination = defaultPagination,
    completed: completedPagination = defaultPagination,
    canceled: cancelledPagination = defaultPagination,
  } = formsPagination;

  const getArchivedDataSource = () => {
    if (archivedValue === FORM_TABS.Voided) return formsData.voidedData;

    if (archivedValue === FORM_TABS.Cancelled) return formsData.cancelledData;

    if (archivedValue === FORM_TABS.Completed) return formsData.completedData;

    return [];
  };

  const getArchivedPagination = () => {
    if (archivedValue === FORM_TABS.Voided) return voidedPagination;

    if (archivedValue === FORM_TABS.Cancelled) return cancelledPagination;

    if (archivedValue === FORM_TABS.Completed) return completedPagination;

    return '';
  };

  const getArchivedOptions = () => {
    return [
      { label: 'Voided', count: isPending ? null : voidedPagination.total },
      { label: 'Completed', count: isPending ? null : completedPagination.total },
    ];
  };

  const archivedDataSource = getArchivedDataSource();

  const renderTable = (dataSource, totalPages, formStatus) => {
    return (
      <FormPaginatedTableWrapper
        formStatus={formStatus}
        dataSource={dataSource}
        tableProps={tableProps}
        emptyText={formsEmptyText}
        totalPages={totalPages}
        collapsibleProps={{ disableCollapse: true, loading: isPending || loading }}
      />
    );
  };

  const tabItems = [
    ...(!isClientOrThidParty
      ? [
          {
            key: FORM_TABS.Draft,
            label: (
              <TabLabel title={FORM_TABS.Draft} count={isPending ? null : draftPagination.total} />
            ),
            children: renderTable(formsData.draftData, draftPagination.total, FormsStatuses.Draft),
          },
        ]
      : []),
    {
      key: FORM_TABS.WaitingOnYou,
      label: (
        <TabLabel
          title={FORM_TABS.WaitingOnYou}
          count={isPending ? null : waitingOnYouPagination.total}
        />
      ),

      children: renderTable(
        formsData.waitingOnYouData,
        waitingOnYouPagination.total,
        FormsStatuses.WaitingOnYou,
      ),
    },
    {
      key: FORM_TABS.WaitingOnOthers,
      label: (
        <TabLabel
          title={FORM_TABS.WaitingOnOthers}
          count={isPending ? null : waitingOnOthersPagination.total}
        />
      ),
      children: renderTable(
        formsData.waitingOnOthersData,
        waitingOnOthersPagination.total,
        FormsStatuses.WaitingOnOthers,
      ),
    },
    {
      key: FORM_TABS.RecentlyCompleted,
      label: (
        <TabLabel
          title={FORM_TABS.RecentlyCompleted}
          count={isPending ? null : recentlyCompletedPagination.total + finalizingPagination.total}
        />
      ),
      children: renderTable(
        formsData.recentlyCompletedData,
        recentlyCompletedPagination.total + finalizingPagination.total,
        FormsStatuses.RecentlyCompleted,
      ),
    },
    {
      key: FORM_TABS.Declined,
      label: (
        <TabLabel title={FORM_TABS.Declined} count={isPending ? null : declinedPagination.total} />
      ),
      children: renderTable(
        formsData.declinedData,
        declinedPagination.total,
        FormsStatuses.Declined,
      ),
    },
    {
      key: FORM_TABS.Archive,
      label: (
        <ArchiveDropdown
          archivedValue={archivedValue}
          setArchivedValue={(value) => {
            setArchivedValue(value);
            setActiveKey(FORM_TABS.Archive);
          }}
          tabCount={getArchivedPagination().total}
          archivedData={archivedDataSource}
          dropdownOptions={getArchivedOptions()}
        />
      ),

      children: archivedValue
        ? renderTable(archivedDataSource, getArchivedPagination().total, archivedValue)
        : null,
    },
  ];

  const resetFormTab = () => {
    activeFormTab && dispatch(setFormsTabEffect(null));
  };

  return (
    <WorkshopPageTabs
      activeKey={activeKey}
      items={tabItems}
      onChange={(value) => {
        resetFormTab();
        if (archivedValue && value !== FORM_TABS.Archive) {
          setArchivedValue('');
        }

        value !== FORM_TABS.Archive && setActiveKey(value);
      }}
    />
  );
};
